/**
 * PRE-IMPORT ANALYSIS
 * 
 * Analyzes mapped import data BEFORE it goes into the database.
 * Detects data quality issues and gives users a chance to fix them.
 * 
 * ISSUE CATEGORIES:
 * 1. Junk Values - "????", "N/A", "unknown", etc.
 * 2. Date-Only Values - "Aug-17", "2024/09" in collection name
 * 3. Tag-Like Data - comma-separated lists in wrong fields
 * 4. Misplaced Data - data that belongs in a different field
 * 
 * Each issue includes:
 * - What's wrong
 * - Which rows are affected
 * - Suggested fix (auto-fix or manual)
 * - Option to ignore
 */

import { isJunkValue } from './importValidation';

// ============================================
// ISSUE TYPES
// ============================================

export type IssueType = 
  | 'junk_value'           // Placeholder/error values like "????"
  | 'date_in_collection'   // Date patterns in collection name field
  | 'tags_in_wrong_field'  // Comma-separated tags in name/brand/collection
  | 'year_only'            // Just a year like "2020" in collection
  | 'empty_required';      // Required field is empty

export type IssueSeverity = 'error' | 'warning' | 'info';

export type FixAction = 
  | 'clear'                // Clear the value (set to empty)
  | 'extract_date'         // Extract to year/season fields
  | 'move_to_tags'         // Move to tags field
  | 'keep'                 // Keep as-is (user choice)
  | 'manual';              // Needs manual review

export interface DataIssue {
  id: string;
  type: IssueType;
  severity: IssueSeverity;
  field: string;
  title: string;
  description: string;
  affectedRows: number[];        // Row indices
  exampleValues: string[];       // Sample values for display
  totalCount: number;            // How many rows affected
  suggestedFix: FixAction;
  fixDescription: string;
  canAutoFix: boolean;
}

export interface PreImportAnalysisResult {
  issues: DataIssue[];
  totalRows: number;
  cleanRows: number;            // Rows with no issues
  rowsWithIssues: number;       // Rows with at least one issue
  issuesByType: Record<IssueType, number>;
  // Quick stats
  hasBlockingIssues: boolean;   // Issues that prevent import
  hasWarnings: boolean;         // Issues user should review
  hasInfoItems: boolean;        // FYI items
}

// ============================================
// DETECTION PATTERNS
// ============================================

// Date patterns in collection names
const DATE_ONLY_PATTERNS = [
  // Mon-YY (Aug-17, Jan-20)
  { pattern: /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[\/\-\s](\d{2})$/i, type: 'month-shortyear' },
  // YYYY/MM or YYYY-MM
  { pattern: /^(20\d{2})[\/\-](\d{1,2})$/, type: 'year-month' },
  // YYYY/Season
  { pattern: /^(20\d{2})[\/\-](Spring|Summer|Fall|Winter)$/i, type: 'year-season' },
  // Season YYYY
  { pattern: /^(Spring|Summer|Fall|Winter)\s+(20\d{2})$/i, type: 'season-year' },
  // YYYY only
  { pattern: /^(20\d{2})$/, type: 'year-only' },
  // Holiday patterns
  { pattern: /^(20\d{2})[\/\-](Holiday|Christmas|Halloween)$/i, type: 'year-holiday' },
];

// Words that, when appearing in a comma-separated list, suggest tags
const TAG_KEYWORDS = new Set([
  // Finishes
  'shimmer', 'glitter', 'holo', 'holographic', 'chrome', 'metallic', 'matte', 
  'creme', 'cream', 'jelly', 'crelly', 'sheer', 'duochrome', 'multichrome',
  // Particles
  'flakes', 'flake', 'flakie', 'flakies', 'glitter', 'sparkle',
  // Features
  'magnetic', 'thermal', 'glow', 'gitd', 'uv', 'neon',
  // Colors (only flag if part of a list)
  'red', 'blue', 'green', 'purple', 'pink', 'orange', 'yellow', 'black', 'white',
]);

// ============================================
// ANALYSIS FUNCTIONS
// ============================================

/**
 * Check if a value looks like a date that belongs in year/season fields
 */
function isDateOnlyValue(value: string): { isDate: boolean; type?: string } {
  if (!value || typeof value !== 'string') return { isDate: false };
  
  const trimmed = value.trim();
  
  for (const { pattern, type } of DATE_ONLY_PATTERNS) {
    if (pattern.test(trimmed)) {
      return { isDate: true, type };
    }
  }
  
  return { isDate: false };
}

/**
 * Check if a value looks like comma-separated tags
 * Only flags if:
 * 1. Contains at least 2 comma-separated items, AND
 * 2. Most items are short (3 words or less) - not full sentences
 */
function looksLikeTags(value: string): boolean {
  if (!value || typeof value !== 'string') return false;
  
  const trimmed = value.trim();
  
  // Must have at least one comma to be considered tag-like
  if (!trimmed.includes(',')) {
    return false;
  }
  
  // Split by comma and check the items
  const items = trimmed.split(',').map(s => s.trim()).filter(s => s.length > 0);
  
  // Need at least 2 items
  if (items.length < 2) {
    return false;
  }
  
  // Check if items look like tags (short, 1-3 words each)
  const shortItems = items.filter(item => {
    const wordCount = item.split(/\s+/).length;
    return wordCount <= 3;
  });
  
  // If most items are short, it's probably a tag list
  // If items are long (like full descriptions), it's probably not tags
  const mostAreShort = shortItems.length >= items.length * 0.7;
  
  // Additional check: if many items match known tag keywords, definitely tags
  const tagMatches = items.filter(item => 
    TAG_KEYWORDS.has(item.toLowerCase())
  ).length;
  
  const hasTagKeywords = tagMatches >= 2 || tagMatches >= items.length * 0.3;
  
  return mostAreShort && (hasTagKeywords || items.length >= 3);
}

/**
 * Analyze a single row for issues
 */
function analyzeRow(row: Record<string, any>, _rowIndex: number): { field: string; issue: Partial<DataIssue> }[] {
  const issues: { field: string; issue: Partial<DataIssue> }[] = [];
  
  // Check collection/collectionName for issues
  const collectionValue = row.collection || row.collectionName || '';
  
  if (collectionValue) {
    // Check for junk values
    if (isJunkValue(collectionValue, 'collectionName')) {
      issues.push({
        field: 'collection',
        issue: {
          type: 'junk_value',
          severity: 'warning',
        }
      });
    }
    // Check for date-only values
    else if (isDateOnlyValue(collectionValue).isDate) {
      issues.push({
        field: 'collection',
        issue: {
          type: 'date_in_collection',
          severity: 'warning',
        }
      });
    }
    // Check for tag-like data
    else if (looksLikeTags(collectionValue)) {
      issues.push({
        field: 'collection',
        issue: {
          type: 'tags_in_wrong_field',
          severity: 'info',
        }
      });
    }
  }
  
  // Check name field for tag-like data
  const nameValue = row.name || '';
  if (nameValue && looksLikeTags(nameValue)) {
    issues.push({
      field: 'name',
      issue: {
        type: 'tags_in_wrong_field',
        severity: 'warning',
      }
    });
  }
  
  // Check brand field for tag-like data
  const brandValue = row.brand || '';
  if (brandValue && looksLikeTags(brandValue)) {
    issues.push({
      field: 'brand',
      issue: {
        type: 'tags_in_wrong_field',
        severity: 'warning',
      }
    });
  }
  
  // Check for junk in other fields
  const fieldsToCheck = ['brand', 'eventSource', 'eventTheme', 'releaseType'];
  for (const field of fieldsToCheck) {
    const value = row[field];
    if (value && isJunkValue(value, field)) {
      issues.push({
        field,
        issue: {
          type: 'junk_value',
          severity: 'warning',
        }
      });
    }
  }
  
  return issues;
}

/**
 * Main analysis function - analyzes all mapped rows
 */
export function analyzePreImport(mappedRows: Record<string, any>[]): PreImportAnalysisResult {
  const issueMap = new Map<string, DataIssue>();
  const rowsWithIssues = new Set<number>();
  const issuesByType: Record<IssueType, number> = {
    junk_value: 0,
    date_in_collection: 0,
    tags_in_wrong_field: 0,
    year_only: 0,
    empty_required: 0,
  };
  
  // Analyze each row
  mappedRows.forEach((row, rowIndex) => {
    const rowIssues = analyzeRow(row, rowIndex);
    
    if (rowIssues.length > 0) {
      rowsWithIssues.add(rowIndex);
    }
    
    for (const { field, issue } of rowIssues) {
      const key = `${issue.type}-${field}`;
      const value = row[field] || row.collection || row.collectionName || '';
      
      if (!issueMap.has(key)) {
        // Create new issue entry
        issueMap.set(key, {
          id: key,
          type: issue.type!,
          severity: issue.severity!,
          field,
          title: getIssueTitle(issue.type!, field),
          description: getIssueDescription(issue.type!, field),
          affectedRows: [rowIndex],
          exampleValues: [value],
          totalCount: 1,
          suggestedFix: getSuggestedFix(issue.type!),
          fixDescription: getFixDescription(issue.type!),
          canAutoFix: canAutoFix(issue.type!),
        });
        issuesByType[issue.type!]++;
      } else {
        // Update existing issue
        const existing = issueMap.get(key)!;
        existing.affectedRows.push(rowIndex);
        existing.totalCount++;
        
        // Add to examples if unique and under limit
        if (existing.exampleValues.length < 5 && !existing.exampleValues.includes(value)) {
          existing.exampleValues.push(value);
        }
      }
    }
  });
  
  const issues = Array.from(issueMap.values());
  
  // Sort issues by severity, then by count
  issues.sort((a, b) => {
    const severityOrder = { error: 0, warning: 1, info: 2 };
    if (severityOrder[a.severity] !== severityOrder[b.severity]) {
      return severityOrder[a.severity] - severityOrder[b.severity];
    }
    return b.totalCount - a.totalCount;
  });
  
  return {
    issues,
    totalRows: mappedRows.length,
    cleanRows: mappedRows.length - rowsWithIssues.size,
    rowsWithIssues: rowsWithIssues.size,
    issuesByType,
    hasBlockingIssues: issues.some(i => i.severity === 'error'),
    hasWarnings: issues.some(i => i.severity === 'warning'),
    hasInfoItems: issues.some(i => i.severity === 'info'),
  };
}

// ============================================
// HELPER FUNCTIONS
// ============================================

function getIssueTitle(type: IssueType, field: string): string {
  const fieldLabel = field === 'collection' ? 'Collection Name' : field;
  
  switch (type) {
    case 'junk_value':
      return `Placeholder values in ${fieldLabel}`;
    case 'date_in_collection':
      return `Dates found in ${fieldLabel}`;
    case 'tags_in_wrong_field':
      return `Tag-like data in ${fieldLabel}`;
    case 'year_only':
      return `Year-only values in ${fieldLabel}`;
    case 'empty_required':
      return `Missing required ${fieldLabel}`;
    default:
      return `Issue in ${fieldLabel}`;
  }
}

function getIssueDescription(type: IssueType, _field: string): string {
  switch (type) {
    case 'junk_value':
      return `Values like "????", "N/A", or "unknown" were found. These will be cleared to keep your data clean.`;
    case 'date_in_collection':
      return `These look like dates (e.g., "Aug-17", "2024/09") rather than collection names. We can extract the year and season to proper fields.`;
    case 'tags_in_wrong_field':
      return `This looks like a list of tags or descriptors. Consider moving to the Tags field for better filtering.`;
    case 'year_only':
      return `Just a year number was found. This can be moved to the Release Year field.`;
    case 'empty_required':
      return `This required field is empty. These rows will be skipped.`;
    default:
      return `An issue was detected with this field.`;
  }
}

function getSuggestedFix(type: IssueType): FixAction {
  switch (type) {
    case 'junk_value':
      return 'clear';
    case 'date_in_collection':
      return 'extract_date';
    case 'tags_in_wrong_field':
      return 'move_to_tags';
    case 'year_only':
      return 'extract_date';
    case 'empty_required':
      return 'manual';
    default:
      return 'keep';
  }
}

function getFixDescription(type: IssueType): string {
  switch (type) {
    case 'junk_value':
      return `Clear these placeholder values (field will be empty)`;
    case 'date_in_collection':
      return `Extract year/season to proper fields and clear collection name`;
    case 'tags_in_wrong_field':
      return `Move to Tags field for better searchability`;
    case 'year_only':
      return `Move to Release Year field`;
    case 'empty_required':
      return `Review and fill in manually, or these rows will be skipped`;
    default:
      return `Keep as-is`;
  }
}

function canAutoFix(type: IssueType): boolean {
  switch (type) {
    case 'junk_value':
    case 'date_in_collection':
    case 'year_only':
      return true;
    case 'tags_in_wrong_field':
    case 'empty_required':
      return false;
    default:
      return false;
  }
}

// ============================================
// FIX APPLICATION
// ============================================

/**
 * Apply fixes to the mapped rows based on user selections
 */
export function applyFixes(
  rows: Record<string, any>[],
  issues: DataIssue[],
  selectedFixes: Record<string, FixAction>
): Record<string, any>[] {
  // Create a deep copy to avoid mutating original
  const fixedRows = rows.map(row => ({ ...row }));
  
  for (const issue of issues) {
    const action = selectedFixes[issue.id] || 'keep';
    
    if (action === 'keep') continue;
    
    for (const rowIndex of issue.affectedRows) {
      const row = fixedRows[rowIndex];
      
      switch (action) {
        case 'clear':
          // Clear the field
          if (issue.field === 'collection') {
            row.collection = '';
            row.collectionName = '';
          } else {
            row[issue.field] = '';
          }
          break;
          
        case 'extract_date':
          // Extract date to year/season and clear collection
          const value = row[issue.field] || row.collection || row.collectionName || '';
          const extracted = extractDateFromValue(value);
          
          if (extracted.year) {
            row.releaseYear = extracted.year;
          }
          if (extracted.season) {
            row.releaseSeason = extracted.season;
          }
          
          // Clear the original field
          if (issue.field === 'collection') {
            row.collection = '';
            row.collectionName = '';
          } else {
            row[issue.field] = '';
          }
          break;
          
        case 'move_to_tags':
          // Move comma-separated values to tags
          const tagValue = row[issue.field] || '';
          if (tagValue) {
            const newTags = tagValue.split(',').map((t: string) => t.trim()).filter((t: string) => t);
            row.tags = [...(row.tags || []), ...newTags];
            row[issue.field] = '';
          }
          break;
      }
    }
  }
  
  return fixedRows;
}

/**
 * Extract year and season from a date-like value
 */
function extractDateFromValue(value: string): { year?: number; season?: string } {
  const monthToSeason: Record<string, string> = {
    'jan': 'Winter', 'feb': 'Winter', 'mar': 'Spring',
    'apr': 'Spring', 'may': 'Spring', 'jun': 'Summer',
    'jul': 'Summer', 'aug': 'Summer', 'sep': 'Fall',
    'oct': 'Fall', 'nov': 'Fall', 'dec': 'Winter',
    '01': 'Winter', '02': 'Winter', '03': 'Spring',
    '04': 'Spring', '05': 'Spring', '06': 'Summer',
    '07': 'Summer', '08': 'Summer', '09': 'Fall',
    '10': 'Fall', '11': 'Fall', '12': 'Winter',
    '1': 'Winter', '2': 'Winter', '3': 'Spring',
    '4': 'Spring', '5': 'Spring', '6': 'Summer',
    '7': 'Summer', '8': 'Summer', '9': 'Fall',
  };
  
  const result: { year?: number; season?: string } = {};
  
  // Mon-YY pattern (Aug-17)
  let match = value.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[\/\-\s](\d{2})$/i);
  if (match) {
    result.year = 2000 + parseInt(match[2]);
    result.season = monthToSeason[match[1].toLowerCase()];
    return result;
  }
  
  // YYYY/MM pattern
  match = value.match(/^(20\d{2})[\/\-](\d{1,2})$/);
  if (match) {
    result.year = parseInt(match[1]);
    result.season = monthToSeason[match[2]];
    return result;
  }
  
  // YYYY/Season or Season YYYY
  match = value.match(/^(20\d{2})[\/\-\s]*(Spring|Summer|Fall|Winter)$/i);
  if (match) {
    result.year = parseInt(match[1]);
    result.season = match[2].charAt(0).toUpperCase() + match[2].slice(1).toLowerCase();
    return result;
  }
  
  match = value.match(/^(Spring|Summer|Fall|Winter)[\/\-\s]*(20\d{2})$/i);
  if (match) {
    result.year = parseInt(match[2]);
    result.season = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
    return result;
  }
  
  // Year only
  match = value.match(/^(20\d{2})$/);
  if (match) {
    result.year = parseInt(match[1]);
    return result;
  }
  
  // Holiday patterns
  match = value.match(/^(20\d{2})[\/\-](Holiday|Christmas)$/i);
  if (match) {
    result.year = parseInt(match[1]);
    result.season = 'Winter';
    return result;
  }
  
  match = value.match(/^(20\d{2})[\/\-]Halloween$/i);
  if (match) {
    result.year = parseInt(match[1]);
    result.season = 'Fall';
    return result;
  }
  
  return result;
}

// ============================================
// UI HELPER FUNCTIONS
// ============================================

/**
 * Get icon/emoji for issue type
 */
export function getIssueIcon(type: IssueType): string {
  switch (type) {
    case 'junk_value': return '🗑️';
    case 'date_in_collection': return '📅';
    case 'tags_in_wrong_field': return '🏷️';
    case 'year_only': return '📆';
    case 'empty_required': return '⚠️';
    default: return '❓';
  }
}

/**
 * Get severity color class
 */
export function getSeverityColor(severity: IssueSeverity): string {
  switch (severity) {
    case 'error': return 'red';
    case 'warning': return 'amber';
    case 'info': return 'blue';
    default: return 'gray';
  }
}

/**
 * Format count with proper pluralization
 */
export function formatCount(count: number, singular: string, plural?: string): string {
  const p = plural || `${singular}s`;
  return `${count} ${count === 1 ? singular : p}`;
}
